import { CircularProgress } from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Subscription } from "rxjs";
import { BirdBowlData } from "../../types/bird/bird-bowl-data";
import { MatchFormat } from "../../types/entities/match-format";
import { EmployeeRoute } from "../../types/route-helpers";
import { services } from "../../types/services";
import { UUID } from "../../types/uuid";
import BallByBallChart from "./ball-by-ball-chart";
import ExpectedHistorics from "./expected-historics";
import FiltersSection from "./filters-section";
import {
  applyInningsAndOverFilters,
  BIN_COUNT,
  BirdDataRange,
  calculateRange,
  filterByAllSpin,
  filterByFingerSpin,
  filterByMetricRange,
  filterBySeam,
  filterByWristSpin,
  getMetricValue,
  HISTOGRAM_COLOR_PALETTE,
  InsightsPageMetric,
} from "./insights-page-utils";
import MaxSpinByOverChart from "./max-spin-by-over-chart";
import MetricComparisonTable from "./metric-comparison-table";
import MetricSelector from "./metric-selector";

ChartJS.register(PointElement, LinearScale, Tooltip, Legend, CategoryScale);

export default function InsightsPage(): React.JSX.Element {
  const [latestBirdUpdates, setLatestBirdUpdates] = useState<BirdBowlData[]>(
    []
  );
  const [historicBirdUpdates, setHistoricBirdUpdates] = useState<
    BirdBowlData[]
  >([]);
  const [matchFormat, setMatchFormat] = useState<MatchFormat>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedMetric, setSelectedMetric] =
    useState<InsightsPageMetric>("bounceHeight");
  const [mergeYears, setMergeYears] = useState<boolean>(false);
  const [selectedInnings, setSelectedInnings] = useState<
    "both" | "first" | "second"
  >("both");
  const [selectedBowlType, setSelectedBowlType] = useState<
    "seam" | "all spin" | "wrist spin" | "finger spin" | "all"
  >("seam");
  const [overRange, setOverRange] = useState<{ start: number; end: number }>({
    start: 0,
    end: 20,
  });

  const fetchMatchData = (matchId: UUID) => {
    if (!matchId) return;

    setLoading(true);

    services.matchService
      .getOne(matchId)
      .then((match) => {
        return Promise.all<void>([
          services.insightsService.getAllBirdDataForMatch(match.matchId),
          services.insightsService.geHistoricForGround(match.groundId),
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const subscriptions: Subscription[] = [
      services.insightsService.lastInsightsSubject.subscribe(
        setLatestBirdUpdates
      ),
      services.insightsService.historicInsightsSubject.subscribe(
        setHistoricBirdUpdates
      ),
      services.currentGameService.currentMatchFormatSubject.subscribe(
        setMatchFormat
      ),
      services.matchService.getMatchIdSubject().subscribe(fetchMatchData),
    ];

    return () =>
      subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, []);

  let filteredHistoricBirdUpdates = applyInningsAndOverFilters(
    historicBirdUpdates,
    overRange,
    selectedInnings
  );

  let filteredLatestBirdUpdates = applyInningsAndOverFilters(
    latestBirdUpdates,
    overRange,
    selectedInnings
  );

  const todaysSpinDeliveries = filteredLatestBirdUpdates.filter((update) =>
    filterByAllSpin(update)
  );

  if (selectedBowlType === "all spin") {
    filteredHistoricBirdUpdates = filteredHistoricBirdUpdates.filter((update) =>
      filterByAllSpin(update)
    );
    filteredLatestBirdUpdates = filteredLatestBirdUpdates.filter((update) =>
      filterByAllSpin(update)
    );
  } else if (selectedBowlType === "seam") {
    filteredHistoricBirdUpdates = filteredHistoricBirdUpdates.filter((update) =>
      filterBySeam(update)
    );
    filteredLatestBirdUpdates = filteredLatestBirdUpdates.filter((update) =>
      filterBySeam(update)
    );
  } else if (selectedBowlType === "finger spin") {
    filteredHistoricBirdUpdates = filteredHistoricBirdUpdates.filter((update) =>
      filterByFingerSpin(update)
    );
    filteredLatestBirdUpdates = filteredLatestBirdUpdates.filter((update) =>
      filterByFingerSpin(update)
    );
  } else if (selectedBowlType === "wrist spin") {
    filteredHistoricBirdUpdates = filteredHistoricBirdUpdates.filter((update) =>
      filterByWristSpin(update)
    );
    filteredLatestBirdUpdates = filteredLatestBirdUpdates.filter((update) =>
      filterByWristSpin(update)
    );
  }
  filteredLatestBirdUpdates?.sort((a, b) => a.sequence - b.sequence);
  const processedUpdates = filteredLatestBirdUpdates.map((update, index) => ({
    ...update,
    processedSequence:
      (update.innings - 1) *
        MatchFormat.getTotalBallsInInnings(1, matchFormat) +
      (update.over - 1) * 6 +
      update.ball,
  }));

  // Filter to deliveries within this many standard deviations of the mean
  const range: BirdDataRange = calculateRange(historicBirdUpdates);
  filteredLatestBirdUpdates = filteredLatestBirdUpdates.filter((update) =>
    filterByMetricRange(update, range)
  );
  filteredHistoricBirdUpdates = filteredHistoricBirdUpdates.filter((update) =>
    filterByMetricRange(update, range)
  );

  // Calculate human-readable form for most recent delivery from sequence number
  const highestSequenceUpdate = processedUpdates[processedUpdates.length - 1];
  let mostRecentDelivery = "";
  if (highestSequenceUpdate) {
    const over = highestSequenceUpdate.over - 1;
    const ball = highestSequenceUpdate.ball;
    mostRecentDelivery = `Most Recent Delivery: ${over}.${ball}`;
  }

  const allByYear: { [key: number]: BirdBowlData[] } = {};
  const groupedByYear: { [key: number]: number[] } = {};
  filteredHistoricBirdUpdates.forEach((update) => {
    if (!groupedByYear[update.year]) {
      groupedByYear[update.year] = [];
      allByYear[update.year] = [];
    }
    groupedByYear[update.year].push(getMetricValue(update, selectedMetric));
    allByYear[update.year].push(update);
  });

  const allMetricValues = filteredHistoricBirdUpdates.map((update) =>
    getMetricValue(update, selectedMetric)
  );

  const minMetric =
    allMetricValues.length > 0 ? Math.min(...allMetricValues) : 0;
  const maxMetric =
    allMetricValues.length > 0 ? Math.max(...allMetricValues) : 0;

  const binSize = (maxMetric - minMetric) / BIN_COUNT || 1;

  const barLabels = Array.from({ length: BIN_COUNT }, (_, i) => {
    const start = (minMetric + i * binSize).toFixed(2);
    const end = (minMetric + (i + 1) * binSize).toFixed(2);
    return `${start}-${end}`;
  });

  const binCenters: number[] = [];
  for (let i = 0; i < BIN_COUNT; i++) {
    const start = parseFloat(barLabels[i].split("-")[0]);
    const end = parseFloat(barLabels[i].split("-")[1]);
    binCenters.push((start + end) / 2);
  }

  const bins = new Array(BIN_COUNT).fill(0);
  const barDatasets = mergeYears
    ? (() => {
        const allHeights = Object.values(groupedByYear).flat();
        allHeights.forEach((height) => {
          const binIndex = Math.min(
            Math.floor((height - minMetric) / binSize),
            BIN_COUNT - 1
          );
          bins[binIndex]++;
        });
        const total = bins.reduce((sum, val) => sum + val, 0) || 1;
        const normalisedBins = bins.map((count) => count / total);
        return [
          {
            label: "All Years Combined",
            data: normalisedBins.map((val, i) => ({
              x: binCenters[i],
              y: val,
            })),
            backgroundColor: "rgba(100, 149, 237, 0.7)",
            barThickness: 8,
            barPercentage: 1.0,
            stack: "overlap",
            categoryPercentage: 1.0,
          },
        ];
      })()
    : Object.entries(groupedByYear).map(([year, heights], index) => {
        const bins = new Array(BIN_COUNT).fill(0);
        heights.forEach((height, height_index) => {
          const binIndex = Math.min(
            Math.floor((height - minMetric) / binSize),
            BIN_COUNT - 1
          );
          bins[binIndex]++;
          allByYear[year][height_index].binIndex = binIndex;
        });
        const total = bins.reduce((sum, val) => sum + val, 0) || 1;
        const normalisedBins = bins.map((count) => count / total);
        return {
          label: `${year}`,
          data: normalisedBins.map((val, i) => ({ x: binCenters[i], y: val })),
          backgroundColor:
            HISTOGRAM_COLOR_PALETTE[index % HISTOGRAM_COLOR_PALETTE.length],
          barThickness: 8,
          barPercentage: 1.0,
          stack: "overlap",
          categoryPercentage: 1.0,
        };
      });

  // Initialize the array to collect strike rates for each bin
  const strikeRateByBin: number[][] = Array.from(
    { length: BIN_COUNT },
    () => []
  );

  // Combine all updates into one dataset
  const combinedUpdates = [
    ...filteredHistoricBirdUpdates,
    ...filteredLatestBirdUpdates,
  ];

  combinedUpdates.forEach((update) => {
    const metricValue = getMetricValue(update, selectedMetric);
    const binIndex = Math.max(
      0,
      Math.min(Math.floor((metricValue - minMetric) / binSize), BIN_COUNT - 1)
    );

    // Collect strikeRate if it exists
    if (update.expectation?.strikeRate !== undefined) {
      strikeRateByBin[binIndex].push(update.expectation.strikeRate);
    }
  });

  const strikeRateLineData: { x: number; y: number }[] = [];

  // convert each bin to average and build line data
  for (let i = 0; i < BIN_COUNT; i++) {
    if (strikeRateByBin[i].length > 10) {
      const avg =
        strikeRateByBin[i].reduce((a, b) => a + b, 0) /
        strikeRateByBin[i].length;
      strikeRateLineData.push({ x: binCenters[i], y: avg });
    }
  }

  // Compute wicketPercentByBin and wicketPercentLineData similar to strike rate
  const wicketPercentByBin: number[][] = Array.from(
    { length: BIN_COUNT },
    () => []
  );

  combinedUpdates.forEach((update) => {
    const metricValue = getMetricValue(update, selectedMetric);
    const binIndex = Math.max(
      0,
      Math.min(Math.floor((metricValue - minMetric) / binSize), BIN_COUNT - 1)
    );

    if (update.expectation?.wicketPercent !== undefined) {
      wicketPercentByBin[binIndex].push(update.expectation.wicketPercent);
    }
  });

  const wicketPercentLineData: { x: number; y: number }[] = [];

  for (let i = 0; i < BIN_COUNT; i++) {
    if (wicketPercentByBin[i].length > 20) {
      const avg =
        wicketPercentByBin[i].reduce((a, b) => a + b, 0) /
        wicketPercentByBin[i].length;
      wicketPercentLineData.push({ x: binCenters[i], y: avg });
    }
  }

  const xPositionsOfHistogram =
    barDatasets.length > 0 ? barDatasets[0].data.map((val, i) => val.x) : [];

  const mappedWicketPercentLineData = new Array(
    xPositionsOfHistogram.length
  ).fill(NaN);

  wicketPercentLineData.forEach((val) => {
    if (!isNaN(val.x)) {
      const index = xPositionsOfHistogram.indexOf(val.x);
      mappedWicketPercentLineData[index] = val.y;
    }
  });

  // empty list of length == xPositionsOfHistogram
  const mappedStrikeRateLineData = new Array(xPositionsOfHistogram.length).fill(
    NaN
  );

  strikeRateLineData.forEach((val, i) => {
    // confirm val.x is not NaN
    if (isNaN(val.x)) {
      return;
    }

    // find the index i such that val.x == xPositionsOfHistogram[i]
    const index = xPositionsOfHistogram.indexOf(val.x);
    mappedStrikeRateLineData[index] = val.y;
  });

  // Now strikeRateByBin[i] contains all strikeRate values for bin i

  const latestBinsRaw = new Array(BIN_COUNT).fill(0);
  filteredLatestBirdUpdates.forEach((update) => {
    const metricValue = getMetricValue(update, selectedMetric);
    const binIndex = Math.min(
      Math.floor((metricValue - minMetric) / binSize),
      BIN_COUNT - 1
    );
    latestBinsRaw[binIndex]++;
  });
  const totalLatest = latestBinsRaw.reduce((sum, val) => sum + val, 0) || 1;
  const normalisedLatestBins = latestBinsRaw.map(
    (count) => count / totalLatest
  );

  barDatasets.push({
    label: "Today",
    data: normalisedLatestBins.map((val, i) => ({ x: binCenters[i], y: val })),
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    barThickness: 8,
    barPercentage: 1.0,
    categoryPercentage: 1.0,
    stack: "overlap",
  });

  barDatasets.push({
    label: "Strike Rates",
    data: mappedStrikeRateLineData,
    borderColor: "rgba(0, 0, 0, 0.7)",
    borderWidth: 2,
    fill: false,
    pointRadius: 0,
    showLine: true,
    type: "line",
    yAxisID: "y2",
    xAxisID: "x",
    barThickness: 8,
    barPercentage: 1.0,
    stack: "overlap",
    categoryPercentage: 1.0,
  } as any);

  barDatasets.push({
    label: "Wicket Percentage",
    data: mappedWicketPercentLineData,
    borderColor: "rgba(0, 0, 255, 0.7)",
    borderWidth: 2,
    fill: false,
    pointRadius: 0,
    showLine: true,
    type: "line",
    yAxisID: "y1",
    xAxisID: "x",
    barThickness: 8,
    barPercentage: 1.0,
    categoryPercentage: 1.0,
    stack: "overlap",
  } as any);

  const barData = {
    labels: barLabels,
    datasets: [...barDatasets],
  };

  const barOptions = {
    plugins: {
      legend: {
        display: true,
      },
      dragData: false,
    },
    scales: {
      x: {
        title: {
          display: false,
        },
        ticks: {
          display: true,
        },
        stacked: true,
      },
      y: {
        title: {
          display: true,
          text: "Frequency",
        },
        stacked: false,
      },
      y1: {
        title: {
          display: true,
          text: "Strike Rate",
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      y2: {
        title: {
          display: true,
          text: "Wicket Percentage",
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const dataSourceCounts: { label: string; count: number }[] = Object.entries(
    groupedByYear
  ).map(([year, values]) => ({
    label: year,
    count: values.length,
  }));

  dataSourceCounts.push({
    label: "Today",
    count: filteredLatestBirdUpdates.length,
  });

  return (
    <EmployeeRoute>
      <div className="insights-page">
        {/* Top Row for Metric Buttons */}
        <div>
          <h2 className="insights-page-header-2">Select a Metric to Display</h2>
          <MetricSelector
            selectedMetric={selectedMetric}
            setSelectedMetric={setSelectedMetric}
            mostRecentDelivery={mostRecentDelivery}
          />
        </div>

        <div className="insights-page-settings-box-2">
          <FiltersSection
            selectedBowlType={selectedBowlType}
            setSelectedBowlType={setSelectedBowlType}
            selectedInnings={selectedInnings}
            setSelectedInnings={setSelectedInnings}
            overRange={overRange}
            setOverRange={setOverRange}
            mergeYears={mergeYears}
            setMergeYears={setMergeYears}
          />
        </div>

        {/* Three-Column Layout */}
        <div className="insights-page-columns">
          {/* Left Column */}
          <div className="insights-page-left-column">
            {!loading && (
              <ExpectedHistorics
                historicUpdates={filteredHistoricBirdUpdates}
              />
            )}
          </div>

          {/* Center Column */}
          <div className="insights-page-center-column">
            {!loading && (
              <div style={{ marginBottom: "1rem" }}>
                <h3 className="insights-page-header-3">
                  Today vs Historic Data
                </h3>

                <div style={{ width: "100%", overflow: "hidden" }}>
                  <Bar data={barData} options={barOptions} />
                </div>
              </div>
            )}
            {!loading && (
              <>
                <div>
                  <h3 className="insights-page-header-3">
                    Ball-by-Ball Overview
                  </h3>
                  <div style={{ width: "100%", overflow: "hidden" }}>
                    <BallByBallChart
                      updates={processedUpdates}
                      selectedMetric={selectedMetric}
                    />
                  </div>
                </div>
                <h3 className="insights-page-header-3">Max Spin by Over</h3>
                <MaxSpinByOverChart deliveries={todaysSpinDeliveries} />
              </>
            )}
            {loading && <CircularProgress />}
            {!loading && latestBirdUpdates.length === 0 && (
              <div className="grey italic">Nothing to see here yet</div>
            )}
          </div>

          {/* Right Column */}
          <div className="insights-page-right-column">
            {!loading && (
              <>
                <MetricComparisonTable
                  latest={filteredLatestBirdUpdates}
                  historic={filteredHistoricBirdUpdates}
                  overRange={overRange}
                  selectedInnings={selectedInnings}
                  applyFilters={applyInningsAndOverFilters}
                />
                <div className="insights-page-sample-count-div">
                  <h4 className="insights-page-header-4">Sample Counts</h4>
                  <ul className="insights-page-list">
                    {dataSourceCounts.map((entry) => (
                      <li key={entry.label}>
                        {entry.label}: {entry.count}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </EmployeeRoute>
  );
}
